import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import type { ListedObject, UserId } from '../types';
import { ApiError, deleteObject, listObjects } from '../lib/api';
import { formatBytes, formatRelative } from '../lib/format';
import { formatPixelsAndRatio, readImageDimensions } from '../lib/dimensions';
import {
  ASPECT_LABELS,
  SORT_LABELS,
  TILE_GAP,
  TILE_MIN_WIDTH,
  TILE_SIZE_LABELS,
  compareItems,
  useGallerySettings,
  type AspectMode,
  type CaptionMode,
  type SortMode,
  type TileSize,
} from '../lib/gallerySettings';
import { Spinner } from './Spinner';
import { Lightbox } from './Lightbox';
import { FileIcon } from './FileIcon';

interface GalleryProps {
  user: UserId;
  refreshKey: number;
  activeFolder: string | null;
  onActiveFolderChange: (folder: string | null) => void;
  onError: (message: string) => void;
  onCopied: (label: string) => void;
}

function splitUserKey(key: string, user: UserId): { folder: string; filename: string } {
  const userPrefix = `${user}/`;
  const rel = key.startsWith(userPrefix) ? key.slice(userPrefix.length) : key;
  const lastSlash = rel.lastIndexOf('/');
  if (lastSlash < 0) return { folder: '', filename: rel };
  return { folder: rel.slice(0, lastSlash), filename: rel.slice(lastSlash + 1) };
}

export function Gallery({
  user,
  refreshKey,
  activeFolder,
  onActiveFolderChange,
  onError,
  onCopied,
}: GalleryProps) {
  const [folders, setFolders] = useState<string[]>([]);
  const [items, setItems] = useState<ListedObject[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<ListedObject | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { settings, update, reset } = useGallerySettings();

  // Single fetch: manifest-backed list returns all of the user's objects plus
  // every top-level folder, regardless of the active filter. Filtering by
  // folder happens client-side here so chip switches are instant.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    listObjects()
      .then((res) => {
        if (cancelled) return;
        setItems(res.objects);
        setFolders(res.folders);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        const message = err instanceof ApiError ? err.message : 'Could not load gallery.';
        setError(message);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const handleDelete = useCallback(
    async (key: string) => {
      try {
        await deleteObject(key);
        setItems((prev) => prev.filter((it) => it.key !== key));
        setSelected(null);
        // If that was the last item in the active folder, drop the chip.
        setFolders((prev) => {
          const remaining = items.filter((it) => it.key !== key);
          const stillExists = (folder: string) =>
            remaining.some((it) => it.folder === folder || it.folder.startsWith(`${folder}/`));
          return prev.filter(stillExists);
        });
      } catch (err) {
        const message = err instanceof ApiError ? err.message : 'Could not delete object.';
        onError(message);
      }
    },
    [items, onError],
  );

  const visibleItems = useMemo(() => {
    let out = items;
    if (activeFolder) {
      out = out.filter(
        (o) => o.folder === activeFolder || o.folder.startsWith(`${activeFolder}/`),
      );
    }
    if (settings.imagesOnly) {
      out = out.filter((o) => o.kind === 'image');
    }
    out = [...out].sort((a, b) => compareItems(a, b, settings.sort));
    return out;
  }, [items, activeFolder, settings.imagesOnly, settings.sort]);

  const gridStyle: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: `repeat(auto-fit, minmax(${TILE_MIN_WIDTH[settings.size]}px, 1fr))`,
    gap: `${TILE_GAP[settings.size]}px`,
  };

  const renderBody = () => {
    if (loading) {
      return (
        <div className="flex items-center gap-2 px-1 py-8 text-sm text-muted">
          <Spinner size={14} />
          <span>Loading gallery…</span>
        </div>
      );
    }
    if (error) {
      return (
        <div className="rounded-md border border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-200">
          {error}
        </div>
      );
    }
    if (visibleItems.length === 0) {
      return (
        <div className="rounded-xl border border-dashed border-app-border p-8 text-center text-sm text-muted">
          {activeFolder ? (
            <>Nothing in <code className="font-mono">{activeFolder}/</code> yet.</>
          ) : settings.imagesOnly ? (
            <>No images here. Toggle off "Images only" to see other files.</>
          ) : (
            <>Your gallery is empty. Upload something above to get started.</>
          )}
        </div>
      );
    }
    return (
      <div style={gridStyle}>
        {visibleItems.map((item) => (
          <GalleryTile
            key={item.key}
            item={item}
            user={user}
            aspect={settings.aspect}
            caption={settings.caption}
            onSelect={setSelected}
          />
        ))}
      </div>
    );
  };

  return (
    <section className="card space-y-4">
      <header className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold">Gallery</h2>
          <p className="text-xs text-muted">
            Everything you've uploaded — synced from R2, visible only to you.
          </p>
        </div>
        {!loading && (
          <span className="pill">
            {visibleItems.length} item{visibleItems.length === 1 ? '' : 's'}
          </span>
        )}
      </header>

      <div className="flex flex-wrap gap-1.5">
        <FolderChip
          label="All"
          active={activeFolder === null}
          onClick={() => onActiveFolderChange(null)}
        />
        {folders.map((f) => (
          <FolderChip
            key={f}
            label={f}
            active={activeFolder === f}
            onClick={() => onActiveFolderChange(f)}
          />
        ))}
      </div>

      <GalleryToolbar
        sort={settings.sort}
        size={settings.size}
        aspect={settings.aspect}
        caption={settings.caption}
        imagesOnly={settings.imagesOnly}
        onSortChange={(v) => update({ sort: v })}
        onSizeChange={(v) => update({ size: v })}
        onAspectChange={(v) => update({ aspect: v })}
        onCaptionChange={(v) => update({ caption: v })}
        onImagesOnlyChange={(v) => update({ imagesOnly: v })}
        onReset={reset}
      />

      {renderBody()}

      {selected && (
        <Lightbox
          object={selected}
          user={user}
          onClose={() => setSelected(null)}
          onDelete={handleDelete}
          onCopied={onCopied}
        />
      )}
    </section>
  );
}

interface FolderChipProps {
  label: string;
  active: boolean;
  onClick: () => void;
}

function FolderChip({ label, active, onClick }: FolderChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
        active
          ? 'border-amber-400 bg-amber-500/10 text-app'
          : 'border-app-border text-muted hover:text-app hover:border-app-muted'
      }`}
    >
      {label}
    </button>
  );
}

interface GalleryToolbarProps {
  sort: SortMode;
  size: TileSize;
  aspect: AspectMode;
  caption: CaptionMode;
  imagesOnly: boolean;
  onSortChange: (v: SortMode) => void;
  onSizeChange: (v: TileSize) => void;
  onAspectChange: (v: AspectMode) => void;
  onCaptionChange: (v: CaptionMode) => void;
  onImagesOnlyChange: (v: boolean) => void;
  onReset: () => void;
}

function GalleryToolbar({
  sort,
  size,
  aspect,
  caption,
  imagesOnly,
  onSortChange,
  onSizeChange,
  onAspectChange,
  onCaptionChange,
  onImagesOnlyChange,
  onReset,
}: GalleryToolbarProps) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border border-app-border bg-app-surface-2/60 px-3 py-2">
      <ToolbarLabel label="Sort">
        <select
          value={sort}
          onChange={(e) => onSortChange(e.target.value as SortMode)}
          className="rounded-md border border-app-border bg-app-surface px-2 py-1 text-xs text-app focus-ring"
          aria-label="Sort order"
        >
          {(Object.keys(SORT_LABELS) as SortMode[]).map((v) => (
            <option key={v} value={v}>
              {SORT_LABELS[v]}
            </option>
          ))}
        </select>
      </ToolbarLabel>

      <ToolbarLabel label="Size">
        <Segmented<TileSize>
          value={size}
          options={[
            { value: 's', label: TILE_SIZE_LABELS.s },
            { value: 'm', label: TILE_SIZE_LABELS.m },
            { value: 'l', label: TILE_SIZE_LABELS.l },
            { value: 'xl', label: TILE_SIZE_LABELS.xl },
          ]}
          onChange={onSizeChange}
        />
      </ToolbarLabel>

      <ToolbarLabel label="Aspect">
        <Segmented<AspectMode>
          value={aspect}
          options={[
            { value: 'crop', label: ASPECT_LABELS.crop },
            { value: 'fit', label: ASPECT_LABELS.fit },
          ]}
          onChange={onAspectChange}
        />
      </ToolbarLabel>

      <ToolbarLabel label="Caption">
        <Segmented<CaptionMode>
          value={caption}
          options={[
            { value: 'always', label: 'On' },
            { value: 'hover', label: 'Hover' },
            { value: 'hidden', label: 'Off' },
          ]}
          onChange={onCaptionChange}
        />
      </ToolbarLabel>

      <button
        type="button"
        onClick={() => onImagesOnlyChange(!imagesOnly)}
        aria-pressed={imagesOnly}
        title={imagesOnly ? 'Showing images only' : 'Showing all file types'}
        className={`rounded-md border px-2 py-1 text-xs font-medium transition ${
          imagesOnly
            ? 'border-amber-400 bg-amber-500/10 text-app'
            : 'border-app-border text-muted hover:text-app hover:border-app-muted'
        }`}
      >
        Images only
      </button>

      <button
        type="button"
        onClick={onReset}
        title="Reset gallery view to defaults"
        className="ml-auto text-xs text-muted underline-offset-2 hover:text-app hover:underline"
      >
        Reset
      </button>
    </div>
  );
}

function ToolbarLabel({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="flex items-center gap-1.5">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted">{label}</span>
      {children}
    </label>
  );
}

interface SegmentedProps<T extends string> {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
}

function Segmented<T extends string>({ value, options, onChange }: SegmentedProps<T>) {
  return (
    <div
      role="radiogroup"
      className="inline-flex overflow-hidden rounded-md border border-app-border bg-app-surface"
    >
      {options.map((opt, i) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(opt.value)}
            className={`px-2 py-1 text-xs font-medium transition ${
              active
                ? 'bg-amber-500/15 text-app'
                : 'text-muted hover:text-app hover:bg-app-surface-2'
            } ${i > 0 ? 'border-l border-app-border' : ''}`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

interface GalleryTileProps {
  item: ListedObject;
  user: UserId;
  aspect: AspectMode;
  caption: CaptionMode;
  onSelect: (item: ListedObject) => void;
}

function GalleryTile({ item, user, aspect, caption, onSelect }: GalleryTileProps) {
  const [dimensions, setDimensions] = useState<{ width: number; height: number } | null>(null);
  const { folder, filename } = splitUserKey(item.key, user);
  const isImage = item.kind === 'image';
  const ratioText = dimensions
    ? formatPixelsAndRatio(dimensions.width, dimensions.height)
    : null;

  const imageFitClass = aspect === 'fit' ? 'object-contain' : 'object-cover';

  return (
    <button
      type="button"
      onClick={() => onSelect(item)}
      className="group relative flex flex-col overflow-hidden rounded-xl border border-app-border bg-app-surface-2 text-left transition hover:border-app-muted focus-ring"
      title={item.key}
    >
      <div className="relative aspect-square w-full overflow-hidden bg-black/40">
        {isImage ? (
          <img
            src={item.url}
            alt={filename}
            loading="lazy"
            className={`h-full w-full ${imageFitClass} transition group-hover:scale-[1.03]`}
            onLoad={(e) => setDimensions(readImageDimensions(e.currentTarget))}
          />
        ) : (
          <div className="grid h-full w-full place-items-center">
            <FileIcon kind={item.kind} filename={filename} size={72} className="h-20 w-20" />
          </div>
        )}

        {caption === 'hover' && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/45 to-transparent px-2.5 py-2 opacity-0 transition-opacity duration-150 group-hover:opacity-100">
            <p className="truncate text-xs font-medium text-white" title={filename}>
              {filename}
            </p>
            <p className="truncate text-[10px] text-white/75">
              {folder ? <span className="text-white/90">{folder}</span> : '(root)'}
              <span className="mx-1">·</span>
              {formatBytes(item.size)}
              {ratioText && (
                <>
                  <span className="mx-1">·</span>
                  {ratioText}
                </>
              )}
            </p>
          </div>
        )}
      </div>

      {caption === 'always' && (
        <div className="px-2.5 py-2">
          <p className="truncate text-xs font-medium" title={filename}>{filename}</p>
          <p className="truncate text-[10px] text-muted">
            {folder ? <span className="text-app/70">{folder}</span> : '(root)'}
            <span className="mx-1">·</span>
            {formatBytes(item.size)}
            {ratioText && (
              <>
                <span className="mx-1">·</span>
                {ratioText}
              </>
            )}
            <span className="mx-1">·</span>
            {formatRelative(Date.parse(item.uploaded))}
          </p>
        </div>
      )}
    </button>
  );
}
